import { createRouter, createWebHistory } from 'vue-router'
import { MetaService } from '../services/MetaService'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: () => import('../views/LandingView.vue'),
    meta: {
      title: 'Scribe11 | Canada-First Accessibility Compliance Workspace',
      description: 'Find WCAG issues, fix them at the source, monitor continuously, and keep audit-ready proof. Built for AODA, ACA, and Canadian digital accessibility requirements.',
      canonical: 'https://www.scribe11.com/'
    }
  },
  {
    path: '/pricing',
    name: 'Pricing',
    component: () => import('../views/PricingView.vue'),
    meta: {
      title: 'Pricing | Scribe11',
      description: 'Transparent CAD pricing for Scribe11 — Free Scan to Agency plans. No USD conversion. 14-day free trial on all paid plans.',
      canonical: 'https://www.scribe11.com/pricing'
    }
  },
  {
    path: '/resources/wcag-explained',
    name: 'WCAGGuide',
    component: () => import('../views/WCAGGuideView.vue'),
    meta: {
      title: 'WCAG 2.2 Explained | Scribe11',
      description: 'A complete guide to Web Content Accessibility Guidelines 2.2 — all success criteria at Level A and AA, explained for Canadian businesses.',
      canonical: 'https://www.scribe11.com/resources/wcag-explained'
    }
  },
  {
    path: '/resources/aoda-guide',
    name: 'AODAGuide',
    component: () => import('../views/AODAGuideView.vue'),
    meta: {
      title: 'AODA Guide for Ontario Businesses | Scribe11',
      description: 'What the Accessibility for Ontarians with Disabilities Act requires — website obligations, WCAG timelines, and enforcement.',
      canonical: 'https://www.scribe11.com/resources/aoda-guide'
    }
  },
  {
    path: '/resources/accessible-canada-act',
    name: 'ACAGuide',
    component: () => import('../views/ACAGuideView.vue'),
    meta: {
      title: 'Accessible Canada Act Guide | Scribe11',
      description: 'What the ACA requires for federally regulated organizations — digital accessibility, accessibility plans, and ACA vs. AODA comparison.',
      canonical: 'https://www.scribe11.com/resources/accessible-canada-act'
    }
  },
  {
    path: '/security',
    name: 'Security',
    component: () => import('../views/SecurityView.vue'),
    meta: {
      title: 'Security & Privacy | Scribe11',
      description: 'How Scribe11 protects your data — TLS, session security, HMAC-signed reports, SSRF protections, and responsible disclosure.',
      canonical: 'https://www.scribe11.com/security'
    }
  },
  {
    path: '/accessibility-statement',
    name: 'AccessibilityStatement',
    component: () => import('../views/AccessibilityStatementView.vue'),
    meta: {
      title: 'Accessibility Statement | Scribe11',
      description: 'Scribe11\'s accessibility statement — our WCAG 2.2 AA conformance status, known limitations, and how to report accessibility barriers.',
      canonical: 'https://www.scribe11.com/accessibility-statement'
    }
  },
  {
    path: '/contact',
    name: 'Contact',
    component: () => import('../views/ContactView.vue'),
    meta: {
      title: 'Contact & Demo | Scribe11',
      description: 'Book a Scribe11 demo, ask about pricing, or discuss your accessibility compliance program with our team.',
      canonical: 'https://www.scribe11.com/contact'
    }
  },
  {
    // Free personal notepad — no account required
    path: '/app',
    name: 'App',
    component: () => import('../views/FreePadView.vue'),
    meta: {
      title: 'Free Notepad | Scribe11',
      description: 'Free accessible document editor with WCAG checking. No account required.',
      canonical: 'https://www.scribe11.com/app'
    }
  },
  {
    // Enterprise workspace — requires business/developer/enterprise account
    path: '/enterprise',
    name: 'Enterprise',
    component: () => import('../views/EnterpriseView.vue'),
    meta: {
      title: 'Enterprise Workspace | Scribe11',
      description: 'Team compliance workspace with full WCAG scanning, AODA readiness, HMAC-signed reports, and SAML SSO.',
      canonical: 'https://www.scribe11.com/enterprise'
    }
  },
  {
    path: '/scan',
    name: 'Scan',
    component: () => import('../views/ScanView.vue'),
    meta: {
      title: 'Free Website Accessibility Scan | Scribe11',
      description: 'Scan any public URL for WCAG 2.2 issues — free, no account required. Covers AODA, ACA, and AMA risk signals. Results in under 60 seconds.',
      canonical: 'https://www.scribe11.com/scan'
    }
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: () => import('../views/PrivacyView.vue'),
    meta: {
      title: 'Privacy Policy | Scribe11',
      description: 'Scribe11 privacy policy — how we collect, use, and protect your data under PIPEDA.',
      canonical: 'https://www.scribe11.com/privacy'
    }
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import('../views/TermsView.vue'),
    meta: {
      title: 'Terms of Service | Scribe11',
      description: 'Scribe11 terms of service — acceptable use, billing, liability, and governing law.',
      canonical: 'https://www.scribe11.com/terms'
    }
  },
  {
    path: '/editor/:id',
    redirect: '/app'
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.afterEach((to) => {
  MetaService.updateMeta(to.meta)
  MetaService.injectJsonLd(to.name)
})

export default router
